import {
  applyUtilityMeasurementDualWrite,
  UTILITY_MEASUREMENTS_LABEL,
  UTILITY_MEASUREMENTS_TYPE,
  type UtilityMeasurementEntry,
} from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import type { GeoItem } from '~/common/types';

// Pure pothole prop helpers (no React/UI imports) so the load/save logic stays
// unit-testable in a node test environment. Re-exported from `./common` for
// backwards-compatible import paths.

/** Canonical survey-coords prop label — matches mobile `reportLayouts.ts` and PDF/print export. */
export const LONGITUDE_LATITUDE_PROP_LABEL = 'Longitude, Latitude';

/**
 * Brief web-only typo (slash) before the label above was aligned with mobile.
 * Kept for read fallback in `getInitialValues` and purge on save so records
 * edited on web during that window don't duplicate props or lose coordinates.
 */
export const DEPRECATED_LONGITUDE_LATITUDE_PROP_LABEL = 'Longitude/Latitude';

export type TPotholeFormValues = {
  potholeNumber: string;
  priority: string;
  holeDepth: string;
  surfaceType: string;
  surfaceThickness: string;
  baseThickness: string;
  soil: string;
  utility: string[];
  targetUtility: string[];
  material: string;
  depthToTop: string;
  depthToBottom: string;
  depth: string;
  size: string;
  utilityOwner: string;
  // Per-utility measurement cards. The legacy single-value fields
  // above are dual-written from entry[0] at save time.
  utilityMeasurements: UtilityMeasurementEntry[];

  // survey results
  northing: string;
  easting: string;
  elevation: string;
  longitudeLatitude: string;
  surveyDate: string;
  surveyedBy: string;

  // notes
  location: string;
  notes: string;
  address: string;
};

/**
 * Maps each scalar pothole form field to its geo-item prop label. Drives the
 * generic load/save loop in `getInitialValues` and `buildPotholeUpdateProps`.
 *
 * `utilityMeasurements` is omitted — it is a structured `UtilityMeasurementEntry[]`
 * stored as one `{ type: 'utilityMeasurements' }` prop, not a flat string field.
 * It is seeded by `deriveDisplayEntries` and serialized separately on save.
 */
export const potholeFieldMap: {
  [key in keyof Omit<TPotholeFormValues, 'utilityMeasurements'>]: string;
} = {
  // general
  potholeNumber: 'PH#',
  priority: 'Priority',
  holeDepth: 'Hole Depth',

  // surface & soil
  surfaceType: 'Surface Type',
  surfaceThickness: 'Surface Thickness',
  baseThickness: 'Base Thickness',
  soil: 'Soil',

  // utility
  utility: 'Utility',
  targetUtility: 'Target Utility',
  material: 'Material',
  depthToTop: 'Depth to Top',
  depthToBottom: 'Depth to Bottom',
  depth: 'Depth',
  size: 'Size',
  utilityOwner: 'Utility Owner',

  // survey results
  northing: 'Northing',
  easting: 'Easting',
  elevation: 'Elevation',
  longitudeLatitude: LONGITUDE_LATITUDE_PROP_LABEL,
  surveyDate: 'Survey Date',
  surveyedBy: 'Surveyed By',

  // notes
  location: 'Location',
  notes: 'Notes',
  address: 'Address',
};

export const potholeFieldConfig: {
  [field: string]: 'string' | 'select' | 'date' | 'number';
} = {
  'PH#': 'string',
  Priority: 'select',
  'Hole Depth': 'number',
  'Surface Type': 'select',
  'Surface Thickness': 'number',
  'Base Thickness': 'number',
  Soil: 'select',
  Utility: 'select',
  'Target Utility': 'select',
  Material: 'select',
  'Depth to Top': 'number',
  'Depth to Bottom': 'number',
  Depth: 'number',
  Size: 'number',
  'Utility Owner': 'string',
  Northing: 'number',
  Easting: 'number',
  Elevation: 'number',
  [LONGITUDE_LATITUDE_PROP_LABEL]: 'string',
  'Survey Date': 'string',
  'Surveyed By': 'string',
  Location: 'string',
  Notes: 'string',
  Address: 'string',
};

/** Maps each geo-item prop label back to its scalar pothole form field (inverse of `potholeFieldMap`). */
const potholeMappings = Object.fromEntries(Object.entries(potholeFieldMap).map(([field, label]) => [label, field]));

/**
 * Builds the `props` payload for a pothole update. Pure (no form/React deps) so
 * both flag branches are unit-testable.
 *
 * `useStructured` is the per-record gate from the dialog
 * (`flag ON || record already has a structured Utility Measurements prop`):
 *  - structured: rebuild scalar props, append the canonical `utilityMeasurements`
 *    prop, then dual-write entry[0] into the legacy single-value props.
 *  - legacy: behave exactly like the pre-feature code — no structured prop is
 *    ever originated here, and any structured prop authored elsewhere passes
 *    through untouched via `geoItem.fields`.
 */
export function buildPotholeUpdateProps({
  data,
  geoItem,
  useStructured,
}: {
  data: TPotholeFormValues;
  geoItem: GeoItem;
  useStructured: boolean;
}): GeoItem['props'] {
  // merge all possible property options
  const allPropLabels = new Set<string>();
  for (const p of geoItem.props) allPropLabels.add(p.label);
  for (const p of Object.keys(potholeMappings)) allPropLabels.add(p);

  const props = Array.from(allPropLabels)
    // Drop the slash-label typo so a save migrates coords onto the canonical
    // comma label mobile and exports expect (see DEPRECATED_* above).
    .filter((p) => p !== DEPRECATED_LONGITUDE_LATITUDE_PROP_LABEL)
    // When writing the structured prop, drop any stale copy — it is rebuilt from
    // form state below. When legacy, leave it so it passes through unchanged.
    .filter((p) => (useStructured ? p !== UTILITY_MEASUREMENTS_LABEL : true))
    .map((p) => {
      const formField = potholeMappings[p] as keyof TPotholeFormValues | undefined;
      if (!!formField && data[formField] !== undefined) {
        return {
          label: p ?? '',
          value: data[formField] ?? '',
          type: potholeFieldConfig[p] ?? 'string',
        };
      }
      return (
        geoItem.fields?.[p] ?? {
          label: p,
          value: '',
          type: potholeFieldConfig[p] ?? 'string',
        }
      );
    }) as GeoItem['props'];

  if (!useStructured) {
    return props;
  }

  // Append the canonical per-utility prop, then dual-write the legacy single-value
  // props from entry[0] (clear-on-empty when no entries) so old mobile clients keep
  // rendering depth/material/etc.
  return applyUtilityMeasurementDualWrite([
    ...props,
    {
      label: UTILITY_MEASUREMENTS_LABEL,
      value: data.utilityMeasurements ?? [],
      type: UTILITY_MEASUREMENTS_TYPE,
    },
  ]) as GeoItem['props'];
}
